import { Injectable, computed, inject, signal } from '@angular/core';
import { injectQuery, queryOptions } from '@ngneat/query';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import { derivedAsync } from 'ngxtension/derived-async';
import {
  ServiceTimelinePayload,
  ServiceTimelineService,
} from './service-timeline.service';
import { ServicesOrderState } from '../services-order.state';

@Injectable()
export class ServiceTimelineState {
  private readonly query = injectQuery();
  private readonly serviceTimelineService = inject(ServiceTimelineService);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly servicesOrderState = inject(ServicesOrderState);

  readonly serviceId = signal<string | null>(null);

  private getTimelineQueryOptions(payload: ServiceTimelinePayload) {
    return queryOptions({
      queryKey: ['service-timeline', payload] as const,
      queryFn: async () =>
        await this.serviceTimelineService.getServiceTimeline(payload),
    });
  }

  readonly timelineQuery = derivedAsync(() => {
    const tenantId = this.tenantUrlUtil.currentTenant();
    const serviceDetailId = this.serviceId();

    if (!tenantId || !serviceDetailId) {
      return Promise.resolve(null);
    }

    const payload: ServiceTimelinePayload = {
      tenantId,
      serviceDetailId,
    };

    return this.query({
      ...this.getTimelineQueryOptions(payload),
      enabled: true,
    }).result$;
  });

  readonly timelineData = computed(() => {
    const timelineResult = this.timelineQuery();
    return timelineResult?.data ?? null;
  });

  readonly steps = computed(() => {
    const timelineData = this.timelineData();
    if (!timelineData) {
      return [];
    }

    const result = this.serviceTimelineService.parseTimelineSteps(timelineData);
    return result.steps;
  });

  readonly isCanceled = computed(() => {
    const timelineData = this.timelineData();
    if (!timelineData) {
      return false;
    }

    const result = this.serviceTimelineService.parseTimelineSteps(timelineData);
    return result.isCanceled;
  });

  readonly isLoading = computed(() => this.timelineQuery()?.isLoading ?? false);
  readonly isError = computed(
    () => this.timelineQuery()?.error?.message ?? null
  );
  readonly isLoaded = computed(
    () => !this.isLoading() && this.steps().length > 0
  );

  requestTimeline(serviceDetailId: string): void {
    this.serviceId.set(serviceDetailId);
  }

  retry(): void {
    const current = this.timelineQuery();
    if (current) {
      current.refetch();
    }
  }
}
